import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import Workflow, { ActionStatus, WorkflowAction, WorkflowCategoryOptions } from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import ChevronDown from 'svg/ChevronDown';
import ChevronRight from 'svg/ChevronRight';

import { actionKindMeta, IDENTITY_VALUES, producerKindForValue } from './actionConfig';
import { ActionStatusBadge, actionStatusDisplay, Tone } from './StatusBadge';

// Stable DOM ids so "waiting on" links can scroll to the blocking step.
export const DEMOGRAPHICS_ANCHOR = 'ir-demographics';
export const categoryAnchor = (key: string) => `ir-category-${key}`;

interface Props {
  category: WorkflowCategoryOptions;
  actions: WorkflowAction[];
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
}

function actionLabel(action: WorkflowAction): string {
  return actionKindMeta(action.kind)?.label ?? action.defaultLabel;
}

// The category's headline status is a roll-up of its actions (design §12): a person
// needed anywhere wins, then done-when-all-done, else still going.
function aggregateStatus(actions: WorkflowAction[]): ActionStatus {
  if (actions.some((a) => a.isActionRequired)) return ActionStatus.ACTION_REQUIRED;
  if (actions.some((a) => a.status === ActionStatus.FLAGGED)) return ActionStatus.FLAGGED;
  if (actions.length > 0 && actions.every((a) => a.isCompleted)) return ActionStatus.COMPLETED;
  if (actions.some((a) => a.status === ActionStatus.RUNNING)) return ActionStatus.RUNNING;
  if (
    actions.length > 0 &&
    actions.every((a) => a.status === ActionStatus.CANCELED || a.status === ActionStatus.SKIPPED)
  )
    return ActionStatus.CANCELED;
  return ActionStatus.PENDING;
}

export default function CategoryCard({ category, actions, workflow, onComplete, isCompleting }: Props) {
  const single = actions.length === 1;
  const [expanded, setExpanded] = useState(actions.some((a) => a.isActionRequired || a.isCompleted));

  const title = single ? actionLabel(actions[0]) : category.name;
  const subtitle = single ? null : actions.map(actionLabel).join(' • ');

  return (
    <Card id={categoryAnchor(category.key)} data-testid={`category-card-${category.key}`}>
      <HeaderButton type='button' onClick={() => setExpanded((e) => !e)} aria-expanded={expanded} $open={expanded}>
        <HeaderLeft>
          {expanded ? <ChevronDown width={12} height={12} /> : <ChevronRight width={12} height={12} />}
          <div>
            <CategoryTitle>{title}</CategoryTitle>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </div>
        </HeaderLeft>
        <ActionStatusBadge status={aggregateStatus(actions)} />
      </HeaderButton>

      {expanded &&
        (single ? (
          <SingleBody>
            <ActionBody action={actions[0]} workflow={workflow} onComplete={onComplete} isCompleting={isCompleting} />
          </SingleBody>
        ) : (
          <Rows>
            {actions.map((action) => (
              <ActionRow key={action.id} data-testid='action-row' data-action-kind={action.kind}>
                <StatusDot $tone={actionStatusDisplay(action.status).tone} />
                <RowMain>
                  <RowTitle>
                    {actionStatusDisplay(action.status).label}: {actionLabel(action)}
                  </RowTitle>
                  <ActionBody action={action} workflow={workflow} onComplete={onComplete} isCompleting={isCompleting} />
                </RowMain>
              </ActionRow>
            ))}
          </Rows>
        ))}
    </Card>
  );
}

// The per-action content: its description, plus the controls/result/wait reason
// that depend on status. Reused for single- and multi-action categories.
function ActionBody({
  action,
  workflow,
  onComplete,
  isCompleting,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
}) {
  const meta = actionKindMeta(action.kind);

  return (
    <>
      {meta?.description && <Muted>{meta.description}</Muted>}

      {action.isCompleted && <CompletedResult action={action} workflow={workflow} />}

      {action.isActionRequired && meta?.options && (
        <ActionRequired>
          {meta.prompt && <BodySmall>{meta.prompt}</BodySmall>}
          <Choices>
            {meta.options.map((option) => (
              <Button
                key={option.label}
                variant={option.tone === 'danger' ? 'outline-danger' : 'primary'}
                size='small'
                disabled={isCompleting}
                onClick={() => onComplete(action.id, option.outputs)}
                data-testid={`action-option-${option.label}`}>
                {option.label}
              </Button>
            ))}
          </Choices>
        </ActionRequired>
      )}

      {action.isPending && <WaitingOn action={action} workflow={workflow} />}
    </>
  );
}

// Explain which step(s) a pending action is waiting on, linkable to that step.
function WaitingOn({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const blockers = blockersFor(action, workflow);

  if (blockers.length === 0) {
    return <Muted>Waiting on earlier steps before this can start.</Muted>;
  }

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <Muted data-testid='waiting-on'>
      Waiting on{' '}
      {blockers.map((blocker, i) => (
        <span key={blocker.id}>
          {i > 0 && (i === blockers.length - 1 ? ' and ' : ', ')}
          <StepLink type='button' onClick={() => scrollTo(blocker.id)}>
            {blocker.label}
          </StepLink>
        </span>
      ))}
      .
    </Muted>
  );
}

// The step(s) that, once done (or whose data is entered), would unblock this action:
// the not-yet-completed producers of each missing required value, plus Patient
// details for any missing demographic (which the pills can supply).
function blockersFor(action: WorkflowAction, workflow: Workflow): { id: string; label: string }[] {
  const requires = actionKindMeta(action.kind)?.requires ?? [];
  const missing = requires.filter((name) => !workflow.valueFor(name)?.value);

  const byId = new Map<string, string>();
  missing.forEach((name) => {
    const producerKind = producerKindForValue(name);
    if (producerKind) {
      const producer = workflow.actions.find((a) => a.kind === producerKind);
      if (producer && !producer.isCompleted) {
        byId.set(categoryAnchor(producer.categoryKey ?? ''), actionKindMeta(producerKind)?.label ?? producerKind);
      }
    }
    if (IDENTITY_VALUES.includes(name)) {
      byId.set(DEMOGRAPHICS_ANCHOR, 'Patient details');
    }
  });

  return Array.from(byId, ([id, label]) => ({ id, label }));
}

function CompletedResult({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const meta = actionKindMeta(action.kind);
  if (!meta?.resultValueName) return null;

  const value = workflow.valueFor(meta.resultValueName)?.value;
  if (!value) return null;

  const label = meta.options?.find((o) => o.outputs[meta.resultValueName!] === value)?.label ?? value;
  return (
    <Result data-testid='action-result'>
      <ResultLabel>Result</ResultLabel>
      <ResultValue>{label}</ResultValue>
    </Result>
  );
}

const Card = styled.div`
  border: 1px solid ${colors.black15};
  border-radius: 8px;
  background: ${colors.white};
  overflow: hidden;
`;

const HeaderButton = styled.button<{ $open: boolean }>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  background: ${colors.black03};
  border: none;
  border-bottom: ${({ $open }) => ($open ? `1px solid ${colors.black10}` : 'none')};
  cursor: pointer;
  text-align: left;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;

  > svg {
    flex-shrink: 0;
    margin-top: 4px;
    color: ${colors.black50};
  }
`;

const CategoryTitle = styled(BodySmallBold)`
  font-size: 15px;
`;

const Subtitle = styled(BodySmall)`
  margin-top: 4px;
  color: ${colors.black50};
`;

const SingleBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Rows = styled.div`
  background: ${colors.white};
`;

const ActionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 16px 20px;

  & + & {
    border-top: 1px solid ${colors.black05};
  }
`;

const StatusDot = styled.span<{ $tone: Tone }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 3px;
  box-sizing: border-box;
  ${({ $tone }) => {
    if ($tone === 'success') return `background: ${colors.actionGreen};`;
    if ($tone === 'warning' || $tone === 'danger' || $tone === 'info') return `background: ${colors.secondaryOrange};`;
    return `border: 1.5px solid ${colors.black25};`;
  }}
`;

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const RowTitle = styled(BodySmallBold)`
  color: ${colors.black};
`;

const ActionRequired = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Choices = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Result = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ResultLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 11px;
`;

const ResultValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.black};
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;

const StepLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${colors.primaryBlue};
  cursor: pointer;
  text-decoration: underline;
`;
